/**
 * Google-Solar-Analyse-Panel für den PV-Konfigurator.
 *
 * - Startet auf Knopfdruck die Google-Solar-Dachanalyse
 * - Zeigt: Luftbild, Dachfläche, max. Panels, Sonnenstunden, Dach-Segmente
 * - Rendert Budget- (Fox ESS) und Premium-Paket-Vorschläge (Sigenergy)
 *
 * Ergänzt (ersetzt NICHT) die bestehende Mapbox+Gemini KI-Dachanalyse.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Satellite, Sun, Ruler, Layers, Loader2, RefreshCw, MapPin,
  ChevronRight, Zap, Package, Info, Plus, CheckCircle2,
  Car, ShieldCheck, Waves,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt0 = (n) => Number(n || 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });
const fmt1 = (n) => Number(n || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 });
const fmtEur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

function Stat({ icon: Icon, label, value, unit, testid }) {
  return (
    <div className="bg-[#0F0F11] border border-zinc-800 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-zinc-400 font-semibold">
        <Icon size={12} />
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-[20px] font-semibold text-zinc-100 font-mono leading-none">{value}</span>
        {unit && <span className="text-[12px] text-zinc-400">{unit}</span>}
      </div>
    </div>
  );
}

function PackageCard({ tier, pkg, testid, onCreate, creating }) {
  const isPremium = tier === "premium";
  const accent = isPremium ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5";
  const badgeColor = isPremium ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
  const btnColor = isPremium ? "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/40 text-amber-200" : "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-200";
  const chipActive = isPremium ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "bg-emerald-500/20 border-emerald-500/60 text-emerald-100";
  const chipInactive = "bg-zinc-800/40 border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100";

  const [extras, setExtras] = useState({ wallbox: false, backup: false, optimizer: false });
  const toggleExtra = (k) => setExtras((s) => ({ ...s, [k]: !s[k] }));
  const activeExtrasList = Object.keys(extras).filter((k) => extras[k]);

  const extraDefs = isPremium
    ? [
        { key: "wallbox", label: "Sigen Wallbox", icon: Car },
        { key: "backup", label: "Sigen Gateway (Notstrom)", icon: ShieldCheck },
        { key: "optimizer", label: "Optimierer je Panel", icon: Waves },
      ]
    : [
        { key: "wallbox", label: "Fox Wallbox", icon: Car },
        { key: "backup", label: "EPS-Box (Notstrom)", icon: ShieldCheck },
        { key: "optimizer", label: "Tigo Optimierer je Panel", icon: Waves },
      ];
  return (
    <div className={`border rounded-xl p-4 ${accent}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[12px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${badgeColor}`}>
              {isPremium ? "Premium" : "Budget"}
            </span>
            <h5 className="text-[15px] font-semibold text-zinc-50">{pkg.name}</h5>
          </div>
          <div className="text-[11.5px] text-zinc-300 mt-0.5">Marke: <b className="text-zinc-100">{pkg.brand}</b></div>
        </div>
        <Package size={16} className={isPremium ? "text-amber-300" : "text-emerald-300"} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[12px] font-medium">
        <div>
          <div className="text-[12px] uppercase text-zinc-400 font-semibold">Panels</div>
          <div className="font-mono text-zinc-100 text-[15px] font-semibold">{fmt0(pkg.panels)} × {pkg.panel_wattage_wp} Wp</div>
        </div>
        <div>
          <div className="text-[12px] uppercase text-zinc-400 font-semibold">Systemgröße</div>
          <div className="font-mono text-zinc-100 text-[15px] font-semibold">{fmt1(pkg.system_kwp)} kWp</div>
        </div>
        <div>
          <div className="text-[12px] uppercase text-zinc-400 font-semibold">Jahresertrag</div>
          <div className="font-mono text-emerald-300 text-[15px] font-semibold">{fmt0(pkg.annual_yield_kwh)} kWh</div>
        </div>
        <div>
          <div className="text-[12px] uppercase text-zinc-400 font-semibold">Dachfläche</div>
          <div className="font-mono text-zinc-300 text-[15px] font-semibold">{fmt1(pkg.roof_area_used_m2)} m²</div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-baseline justify-between">
        <span className="text-[12px] text-zinc-400 font-medium">Zielpreis (netto, 0 % USt)</span>
        <span className={`font-mono font-semibold text-[17px] ${isPremium ? "text-amber-300" : "text-emerald-300"}`}>
          {fmtEur(pkg.estimated_price_gross_eur)}
        </span>
      </div>
      <p className="text-[10.5px] text-zinc-500 mt-2 italic">
        PV-Anlagen für Wohngebäude sind nach § 12 Abs. 3 UStG mit 0 % USt belegt — Netto = Brutto. Richtwert für das Verkaufsgespräch; der finale Preis wird aus dem Paket-Editor (Admin) übernommen.
      </p>

      {/* Iter88e — Was steckt drin? Komponenten-Liste je Paket-Tier */}
      {Array.isArray(pkg.components) && pkg.components.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800/50" data-testid={`${testid}-components`}>
          <div className="text-[12px] uppercase tracking-widest text-zinc-400 font-semibold mb-1.5">Enthaltene Komponenten</div>
          <ul className="space-y-1">
            {pkg.components.map((c, idx) => (
              <li key={idx} className="flex items-start gap-2 text-[12.5px] text-zinc-200 font-medium leading-snug">
                <span className={`mt-1.5 h-1 w-1 rounded-full flex-shrink-0 ${isPremium ? "bg-amber-300" : "bg-emerald-300"}`} />
                <span>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Extras-Chips: optional aktivierbar VOR Paket-Erstellung */}
      <div className="mt-3 pt-3 border-t border-zinc-800/50">
        <div className="text-[12px] uppercase tracking-widest text-zinc-400 font-semibold mb-1.5">Optionale Extras</div>
        <div className="flex flex-wrap gap-1.5">
          {extraDefs.map((e) => {
            const Icn = e.icon;
            const active = extras[e.key];
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => toggleExtra(e.key)}
                data-testid={`${testid}-extra-${e.key}`}
                className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-[10.5px] font-semibold transition ${active ? chipActive : chipInactive}`}
                title={active ? "Wird zum Paket hinzugefügt" : "Klicken zum Hinzufügen"}
              >
                <Icn size={11} />
                {active ? "✓ " : "+ "}{e.label}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onCreate?.(tier, activeExtrasList)}
        disabled={creating}
        data-testid={`${testid}-create`}
        className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 border text-[12px] font-semibold px-3 py-2 rounded-lg disabled:opacity-50 ${btnColor}`}
      >
        {creating ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
        {creating
          ? "Erstelle Paket…"
          : activeExtrasList.length > 0
            ? `Als PV-Paket übernehmen (+${activeExtrasList.length} Extra${activeExtrasList.length > 1 ? "s" : ""})`
            : "Als PV-Paket übernehmen"}
      </button>
    </div>
  );
}
// Größen-Auswahl mit 4 Chips (5 / 10 / 15 / 20 kWp) — deaktiviert wenn Dach zu klein.
// Beim Klick werden darunter Budget-/Premium-Varianten für die gewählte Größe angezeigt.
function SizePicker({ packages, creating, onCreate }) {
  const opts = Array.isArray(packages?.size_options) ? packages.size_options : [];
  // Sinnvoller Default: 10 kWp wenn plausibel, sonst die größte plausible, sonst die erste
  const defaultOpt =
    opts.find((o) => o.target_kwp === 10 && o.plausible) ||
    [...opts].reverse().find((o) => o.plausible) ||
    opts[0];
  const [selected, setSelected] = useState(defaultOpt?.target_kwp || 10);
  const active = opts.find((o) => o.target_kwp === selected) || defaultOpt;
  const maxKwp = Number(packages?.max_possible_kwp || 0);

  if (!active) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Package size={14} className="text-emerald-300" />
          <h4 className="text-[13px] font-semibold text-zinc-100">Paket-Größe wählen</h4>
        </div>
        <span className="text-[10.5px] text-zinc-400">
          Max. auf Ihrem Dach: <b className="text-zinc-200 font-mono">{maxKwp.toFixed(1)} kWp</b>
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3" data-testid="size-picker">
        {opts.map((o) => {
          const isActive = o.target_kwp === selected;
          const disabled = !o.plausible;
          return (
            <button
              key={o.target_kwp}
              type="button"
              onClick={() => !disabled && setSelected(o.target_kwp)}
              disabled={disabled}
              data-testid={`size-chip-${o.target_kwp}`}
              className={`rounded-xl border py-3 px-2 text-center transition ${
                disabled
                  ? "border-zinc-800 bg-zinc-900/40 text-zinc-600 cursor-not-allowed"
                  : isActive
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-100 shadow-[0_0_0_1px_rgba(0,229,117,0.35)]"
                    : "border-zinc-700 bg-[#0F0F11] text-zinc-200 hover:border-emerald-500/60 hover:text-emerald-100"
              }`}
              title={disabled ? `Ihr Dach bietet nur Platz für ~${maxKwp.toFixed(0)} kWp` : `${o.target_kwp} kWp · ${o.panels_needed} Panels · ~${o.area_needed_m2} m²`}
            >
              <div className={`text-[22px] leading-none font-semibold font-mono ${isActive ? "text-emerald-200" : ""}`}>
                {o.target_kwp}
              </div>
              <div className="text-[12px] uppercase tracking-wider mt-1 opacity-80">kWp</div>
              <div className="text-[12px] mt-1.5 text-zinc-300">
                {o.panels_needed} Panels
                {disabled && <div className="text-amber-500/70 mt-0.5 not-italic">Dach zu klein</div>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Budget + Premium für die gewählte Größe */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PackageCard tier="budget" pkg={active.budget} testid="pkg-budget"
          onCreate={(_, extras) => onCreate("budget", extras, active.target_kwp)}
          creating={creating === "budget"} />
        <PackageCard tier="premium" pkg={active.premium} testid="pkg-premium"
          onCreate={(_, extras) => onCreate("premium", extras, active.target_kwp)}
          creating={creating === "premium"} />
      </div>
      <p className="text-[10.5px] text-zinc-500 mt-2 flex items-center gap-1">
        <Info size={11} />
        Berechnungsgrundlage: {packages.panel_wattage_wp} Wp Panel-Leistung · Performance-Ratio {(packages.performance_ratio * 100).toFixed(0)} % · {packages.sun_hours_per_year} h Sonne/Jahr
      </p>
    </div>
  );
}



export default function GoogleSolarPanel({ projectId, onPackageCreated, onChanged }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [creatingTier, setCreatingTier] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);
  const [address, setAddress] = useState(null);
  const [error, setError] = useState(null);
  const [roofImgUrl, setRoofImgUrl] = useState(null);
  // Manueller Dach-Modus (Fallback wenn Google keine Daten hat)
  const [manualMode, setManualMode] = useState(false);
  const [manualRoofArea, setManualRoofArea] = useState("80");
  const [manualSunHours, setManualSunHours] = useState("1050");
  const [manualOrient, setManualOrient] = useState("S");
  const [manualTilt, setManualTilt] = useState("30");
  const [submittingManual, setSubmittingManual] = useState(false);

  const loadRoofImage = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/pv-solar/roof.png`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setRoofImgUrl(url);
    } catch {
      setRoofImgUrl(null);
    }
  }, [projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/projects/${projectId}/pv-solar`);
      if (r.data?.exists) {
        setAnalysis(r.data.analysis);
        setAddress(r.data.analysis?.input_address || null);
        if (r.data.analysis?.imagery?.rgb_url) loadRoofImage();
      } else {
        setAddress(r.data?.address || null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Konnte Analyse nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [projectId, loadRoofImage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (roofImgUrl) URL.revokeObjectURL(roofImgUrl); }, [roofImgUrl]);

  const createPackage = async (tier, extras = [], targetKwp = null) => {
    setCreatingTier(tier);
    try {
      const r = await api.post(`/projects/${projectId}/pv-solar/create-package`, {
        tier,
        extras,
        ...(targetKwp ? { target_kwp: targetKwp } : {}),
      });
      setLastCreated({ tier, ...r.data });
      const extrasCount = (r.data.picked?.extras || []).length;
      const kwLabel = targetKwp ? ` ${targetKwp} kWp` : "";
      toast.success(
        `${tier === "premium" ? "Premium" : "Budget"}${kwLabel}-Paket erstellt${extrasCount > 0 ? ` (+${extrasCount} Extra${extrasCount > 1 ? "s" : ""})` : ""}`
      );
      // Auto-Redirect: Paket-Items direkt in den Projekt-Konfigurator übernehmen,
      // damit der Vertriebsmitarbeiter ohne Zusatzklick weiter bearbeiten kann.
      onPackageCreated?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Paket-Erstellung fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setCreatingTier(null);
    }
  };

  const submitManual = async () => {
    const area = Number(String(manualRoofArea).replace(",", "."));
    if (!area || area <= 0 || area > 10000) {
      toast.error("Dachfläche muss zwischen 1 und 10.000 m² sein");
      return;
    }
    setSubmittingManual(true);
    try {
      const r = await api.post(`/projects/${projectId}/pv-solar/manual`, {
        roof_area_m2: area,
        sun_hours_per_year: Number(manualSunHours) || 1050,
        orientation: manualOrient || null,
        tilt_deg: Number(manualTilt) || null,
      });
      setAnalysis(r.data.analysis);
      setError(null);
      setManualMode(false);
      onChanged?.();
      toast.success("Manuelle Dachdaten übernommen — Pakete werden berechnet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manueller Modus fehlgeschlagen");
    } finally {
      setSubmittingManual(false);
    }
  };

  // Übernimmt Werte aus der bestehenden KI-Dachanalyse (Mapbox+Gemini) direkt
  // in das Manuelle-Modus-Formular. Ein Klick statt 4 Eingaben.
  const importFromAiAnalysis = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/roof-analysis`);
      const active = r.data?.analysis;
      const ai = active?.ai_result;
      if (!active || !ai) {
        toast.error("Keine KI-Dachanalyse vorhanden. Starte sie oben zuerst.");
        return;
      }
      const area = ai.usable_roof_area_m2 || 0;
      if (area > 0) setManualRoofArea(String(area));
      const orientMap = { N: "N", NE: "SO", E: "O", SE: "SO", S: "S", SW: "SW", W: "W", NW: "N", mixed: "S" };
      const o = orientMap[ai.orientation] || "S";
      setManualOrient(o);
      // Neigung liefert die KI nicht — Default 30° bleibt.
      toast.success(`Aus KI-Analyse übernommen: ${area} m² · Ausrichtung ${ai.orientation}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Analyse konnte nicht geladen werden");
    }
  };

  const runAnalysis = async (force = false) => {
    setRunning(true);
    setError(null);
    try {
      const endpoint = force ? "refresh" : "";
      const url = `/projects/${projectId}/pv-solar${endpoint ? "/" + endpoint : ""}`;
      const r = await api.post(url);
      setAnalysis(r.data.analysis);
      onChanged?.();
      if (r.data.analysis?.imagery?.rgb_url) loadRoofImage();
      toast.success("Google-Solar-Analyse fertig");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Analyse fehlgeschlagen.";
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="google-solar-panel-loading">
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <Loader2 className="animate-spin" size={14} /> Lade Solar-Analyse…
        </div>
      </section>
    );
  }

  const m = analysis?.metrics || {};
  const packages = analysis?.packages || {};
  const imagery = analysis?.imagery || {};
  const hasRoofImage = !!imagery?.rgb_url;

  return (
    <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="google-solar-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Satellite size={18} className="text-emerald-300 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-zinc-50">Google Solar-Dachanalyse</h3>
            <p className="text-[11.5px] text-zinc-400 mt-0.5">
              Offizielle Google-Daten: Dachfläche, Sonneneinstrahlung, max. Panels & Paket-Vorschläge
            </p>
          </div>
        </div>
        {analysis && (
          <button
            type="button"
            onClick={() => runAnalysis(true)}
            disabled={running}
            data-testid="google-solar-refresh-btn"
            className="text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw size={12} className={running ? "animate-spin" : ""} />
            Neu analysieren
          </button>
        )}
      </div>

      {/* Address preview */}
      {address && (
        <div className="text-[11.5px] text-zinc-400 flex items-center gap-1.5" data-testid="google-solar-address">
          <MapPin size={12} className="text-zinc-500" />
          <span>{[address.street, address.zip, address.city].filter(Boolean).join(", ")}</span>
        </div>
      )}

      {/* Error */}
      {error && !manualMode && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="google-solar-error">
          <div className="text-[12px] text-rose-200 mb-2 font-medium"><b>Hinweis:</b> {error}</div>
          <button
            type="button"
            onClick={() => setManualMode(true)}
            data-testid="solar-open-manual"
            className="text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 hover:underline"
          >
            → Manuellen Dach-Modus verwenden
          </button>
        </div>
      )}

      {/* Manueller Dach-Modus (Formular) */}
      {manualMode && (
        <div className="bg-[#0F0F11] border border-emerald-500/30 rounded-xl p-4 space-y-3" data-testid="solar-manual-form">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Ruler size={14} className="text-emerald-300" />
              <h4 className="text-[13px] font-semibold text-zinc-100">Manueller Dach-Modus</h4>
            </div>
            <button type="button" onClick={() => setManualMode(false)} className="text-[12px] text-zinc-400 hover:text-zinc-200">
              Abbrechen
            </button>
          </div>
          <p className="text-[11.5px] text-zinc-400">
            Wenn Google Solar für diese Adresse keine Daten hat, gib die Dachfläche selbst ein. Die 5/10/15/20-kWp-Pakete werden dann daraus berechnet.
          </p>
          <button
            type="button"
            onClick={importFromAiAnalysis}
            data-testid="solar-manual-import-ai"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 hover:border-sky-500/50 text-sky-300 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg"
            title="Übernimmt Dachfläche + Ausrichtung aus der bereits durchgeführten KI-Dachanalyse (Mapbox + Gemini)"
          >
            🤖 Aus KI-Dachanalyse übernehmen
          </button>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Nutzbare Dachfläche</label>
              <div className="relative">
                <input
                  type="text" inputMode="decimal"
                  value={manualRoofArea}
                  onChange={(e) => setManualRoofArea(e.target.value)}
                  data-testid="solar-manual-area"
                  className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded pl-2 pr-8 text-[12px] font-mono text-zinc-100 focus:border-emerald-500 outline-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-zinc-300">m²</span>
              </div>
            </div>
            <div>
              <label className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Sonnenstunden/Jahr</label>
              <div className="relative">
                <input
                  type="text" inputMode="numeric"
                  value={manualSunHours}
                  onChange={(e) => setManualSunHours(e.target.value)}
                  data-testid="solar-manual-sun"
                  className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded pl-2 pr-8 text-[12px] font-mono text-zinc-100 focus:border-emerald-500 outline-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-zinc-300">h</span>
              </div>
              <span className="text-[9.5px] text-zinc-500 mt-0.5 block">Ø DE: 1050</span>
            </div>
            <div>
              <label className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Ausrichtung</label>
              <select
                value={manualOrient}
                onChange={(e) => setManualOrient(e.target.value)}
                data-testid="solar-manual-orient"
                className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded px-2 text-[12px] text-zinc-100 focus:border-emerald-500 outline-none font-medium"
              >
                <option value="S">Süd</option>
                <option value="SO">Süd-Ost</option>
                <option value="SW">Süd-West</option>
                <option value="O">Ost</option>
                <option value="W">West</option>
                <option value="N">Nord</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Neigung</label>
              <div className="relative">
                <input
                  type="text" inputMode="decimal"
                  value={manualTilt}
                  onChange={(e) => setManualTilt(e.target.value)}
                  data-testid="solar-manual-tilt"
                  className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded pl-2 pr-8 text-[12px] font-mono text-zinc-100 focus:border-emerald-500 outline-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-zinc-300">°</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={submitManual}
            disabled={submittingManual}
            data-testid="solar-manual-submit"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {submittingManual ? <Loader2 className="animate-spin" size={14} /> : <Ruler size={14} />}
            {submittingManual ? "Berechne…" : "Pakete berechnen"}
          </button>
        </div>
      )}

      {/* Empty state — no analysis yet */}
      {!analysis && !error && !manualMode && (
        <div className="bg-[#0F0F11] border border-dashed border-zinc-700 rounded-xl p-6 text-center" data-testid="google-solar-empty">
          <Satellite size={28} className="mx-auto text-zinc-600 mb-2" />
          <p className="text-[13px] text-zinc-300 mb-3">
            Google Solar analysiert das Dach mit Satelliten-Daten und schlägt passende Pakete vor.
          </p>
          <button
            type="button"
            onClick={() => runAnalysis(false)}
            disabled={running || !address}
            data-testid="google-solar-start-btn"
            className="inline-flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {running ? <Loader2 className="animate-spin" size={14} /> : <Satellite size={14} />}
            {running ? "Analysiere…" : "Dachanalyse starten"}
          </button>
          {!address && (
            <p className="text-[12px] text-amber-300 mt-2">Kundenadresse (Strasse + PLZ + Ort) fehlt.</p>
          )}
          <div className="mt-3 pt-3 border-t border-zinc-800/60">
            <button
              type="button"
              onClick={() => setManualMode(true)}
              data-testid="solar-open-manual-empty"
              className="text-[11.5px] text-zinc-400 hover:text-emerald-300 hover:underline"
            >
              Keine Google-Daten vorhanden? → Manueller Dach-Modus
            </button>
          </div>
        </div>
      )}

      {/* Analysis result */}
      {analysis && (
        <div className="space-y-4" data-testid="google-solar-result">
          {/* Roof image + core stats */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
            {hasRoofImage && (
              <div className="rounded-lg overflow-hidden border border-zinc-800 bg-[#0F0F11] relative min-h-[180px] flex items-center justify-center" data-testid="google-solar-roof-image">
                {roofImgUrl ? (
                  <img
                    src={roofImgUrl}
                    alt="Dach-Luftbild"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Loader2 className="animate-spin text-zinc-500" size={20} />
                )}
                <div className="absolute bottom-1 right-1 text-[9px] text-white/80 bg-black/60 px-1.5 py-0.5 rounded">
                  © Google · {m.imagery_quality || "HIGH"}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Stat icon={Ruler} label="Dachfläche gesamt" value={fmt1(m.total_roof_area_m2)} unit="m²" testid="stat-roof-area" />
              <Stat icon={Layers} label="Nutzbare PV-Fläche" value={fmt1(m.max_array_area_m2)} unit="m²" testid="stat-usable-area" />
              <Stat icon={Zap} label="Max. Panels" value={fmt0(m.max_array_panels_count)} unit="Stk." testid="stat-max-panels" />
              <Stat icon={Sun} label="Sonnenstunden" value={fmt0(m.max_sunshine_hours_per_year)} unit="h / Jahr" testid="stat-sun-hours" />
            </div>
          </div>

          {/* Segments summary */}
          {Array.isArray(m.roof_segments) && m.roof_segments.length > 0 && (
            <details className="bg-[#0F0F11] border border-zinc-800 rounded-lg p-3" data-testid="google-solar-segments">
              <summary className="cursor-pointer text-[12px] font-semibold text-zinc-300 flex items-center gap-1.5">
                <ChevronRight size={12} />
                {m.roof_segments.length} Dach-Segmente identifiziert
              </summary>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[11.5px]">
                  <thead>
                    <tr className="text-zinc-400 font-semibold border-b border-zinc-800">
                      <th className="text-left py-1.5">Nr.</th>
                      <th className="text-right py-1.5">Fläche m²</th>
                      <th className="text-right py-1.5">Neigung</th>
                      <th className="text-right py-1.5">Ausrichtung</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-100 font-mono">
                    {m.roof_segments.slice(0, 12).map((s, i) => (
                      <tr key={i} className="border-b border-zinc-800/50">
                        <td className="py-1">{i + 1}</td>
                        <td className="text-right">{fmt1(s.area_m2)}</td>
                        <td className="text-right">{s.pitch_deg}°</td>
                        <td className="text-right"><span className="font-semibold text-emerald-300">{s.direction}</span> ({fmt0(s.azimuth_deg)}°)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {m.roof_segments.length > 12 && (
                  <p className="text-[12px] text-zinc-300 mt-1 italic">
                    … {m.roof_segments.length - 12} weitere Segmente ausgeblendet
                  </p>
                )}
              </div>
            </details>
          )}

          {/* Package proposals — Größen-Auswahl 5/10/15/20 kWp */}
          {packages?.applicable ? (
            <SizePicker
              packages={packages}
              creating={creatingTier}
              onCreate={createPackage}
            />
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[12px] text-amber-200 font-medium">
              Kein Paket-Vorschlag möglich: {packages?.reason || "Dach nicht auswertbar."}
            </div>
          )}

          {/* Erfolgs-Banner nach Paket-Erstellung */}
          {lastCreated && (
            <div className="mt-3 bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3" data-testid="pkg-created-banner">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1 text-[12px] text-zinc-100 font-medium">
                  <div className="font-semibold text-emerald-200">
                    {lastCreated.tier === "premium" ? "Premium" : "Budget"}-Paket erstellt: {lastCreated.package?.name}
                  </div>
                  <div className="text-[12px] text-zinc-300 mt-1 space-y-0.5">
                    <div>• Modul: <b className="text-zinc-100">{lastCreated.picked?.module?.brand} {lastCreated.picked?.module?.model}</b></div>
                    <div>• Wechselrichter: <b className="text-zinc-100">{lastCreated.picked?.inverter?.brand} {lastCreated.picked?.inverter?.model}</b></div>
                    <div>• Speicher: <b className="text-zinc-100">{lastCreated.picked?.battery?.brand} {lastCreated.picked?.battery?.model}</b></div>
                    {(lastCreated.picked?.extras || []).map((ex, i) => (
                      <div key={i} className="text-emerald-200">
                        + {ex.label}: <b className="text-zinc-100">{ex.brand} {ex.model}</b>
                        {ex.qty > 1 && <span className="text-zinc-400"> · {ex.qty}× je Panel</span>}
                      </div>
                    ))}
                    {(lastCreated.skipped_extras || []).length > 0 && (
                      <div className="text-amber-300 text-[10.5px] mt-1 italic">
                        ⚠ Nicht im Katalog gefunden (übersprungen): {lastCreated.skipped_extras.join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 pt-2 border-t border-emerald-500/20 flex items-center justify-between">
                    <span className="text-[12px] text-zinc-400">
                      Preis <b className="text-emerald-300 font-mono">{fmtEur(lastCreated.totals?.net)}</b>
                      <span className="text-zinc-500 ml-1">(netto = brutto · 0 % USt)</span>
                    </span>
                    <a
                      href="/admin/pv-packages"
                      className="text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 hover:underline"
                      data-testid="pkg-created-goto-editor"
                    >
                      Zum Paket-Editor →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-800/60 text-[10.5px] text-zinc-500">
            <span>Google Solar API · {m.imagery_date?.year ? `Bilddatum ${m.imagery_date.year}` : "aktuell"}</span>
            {analysis.from_cache && <span className="italic">aus Cache</span>}
          </div>
        </div>
      )}
    </section>
  );
}
